import React, {
    forwardRef,
    useState,
    useMemo,
    useCallback,
    useEffect,
} from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';

const DependencyTrackMappingForm = forwardRef(
    function DependencyTrackMappingForm(
        { disableProjectFields = false, ...props },
        ref
    ) {
        const [project, setProject] = useState([]);
        const [projectList, setProjectList] = useState(() => {
            if (props.record) {
                const { projectName } = props.record;

                const projectNamesArray = projectName.split(',');
                const projectNamesSet = new Set();

                projectNamesArray.forEach((name) => {
                    const [projectName] = name?.split(';');
                    projectNamesSet.add(projectName);
                });
                const projectList = Array.from(projectNamesSet).map(
                    (projectName) => ({
                        id: projectName,
                        name: projectName,
                    })
                );

                return projectList;
            }
            return [];
        });
        const [form] = Form.useForm();

        const [loading, setLoading] = useState(false);
        const [initialValues] = useState(() => {
            if (props.record) {
                return undefined;
            }
            return [
                {
                    product: props.defaultProduct,
                    subProduct: props.defaultSubProduct,
                    subProductId: props.defaultSubProductId,
                    environment: props.defaultEnvironment,
                },
            ];
        });

        const parseGroupedProjects = (projects) => {
            const groupedProjects = projects.reduce((acc, project) => {
                const [projectName, version] = project.name.split(';');
                if (!acc[projectName]) {
                    acc[projectName] = [];
                }
                acc[projectName].push({
                    id: project.id,
                    name: projectName + '/' + version,
                });
                return acc;
            }, {});
            setProject(groupedProjects);
        };

        const parseProjectListData = (projects) => {
            const projectListData = projects.reduce((acc, project) => {
                const [projectName] = project.name.split(';');
                if (!acc.some((item) => item.id === projectName)) {
                    acc.push({ id: projectName, name: projectName });
                }
                return acc;
            }, []);

            setProjectList(projectListData);
        };

        useEffect(() => {
            setLoading(true);
            const { promise, cancel } =
                ToolsIntegrationsAPI.getGenericProjectByToolName(
                    props.toolName,
                    {
                        login_id: props.loginConfigId,
                    }
                );
            promise
                .then((response) => {
                    const { projects } = response;

                    parseGroupedProjects(projects);
                    parseProjectListData(projects);
                })
                .catch(() => {})
                .finally(() => setLoading(false));

            return cancel;
        }, [props.loginConfigId, props.toolName]);

        const getFilteredProjectOptions = useCallback(
            (projectNames) => {
                if (_.isEmpty(project)) {
                    return;
                }

                let filteredProjects = [];

                _.forEach(projectNames, (projectName) => {
                    const name = projectName?.split(';')[0];

                    const projectNameVersionList = project[name];

                    filteredProjects = [
                        ...filteredProjects,
                        ...(projectNameVersionList || []),
                    ];
                });
                const projectOptions = filteredProjects?.map((project) => ({
                    label: project.name,
                    value: project.id,
                }));
                return parseSelectOptions(projectOptions);
            },
            [project]
        );

        const getExtraFieldsData = useCallback(
            (config) => {
                const { projectName, version } = config;

                const result = projectName.reduce(
                    (acc, name) => {
                        const projectVersions = project[name];
                        let versionFound = false;
                        if (projectVersions) {
                            projectVersions.forEach((versionProject) => {
                                if (
                                    version &&
                                    version.includes(versionProject.id)
                                ) {
                                    acc.projectIds.push(versionProject.id);
                                    const nameParts =
                                        versionProject.name.split('/');
                                    const versionProjectName = nameParts.find(
                                        (_, index) =>
                                            index === nameParts.length - 1
                                    );
                                    acc.projectNames.push(
                                        `${name};${versionProjectName}`
                                    );
                                    versionFound = true;
                                }
                            });
                            if (!versionFound) {
                                // If no matching version or projectName does not exist in projectMap
                                const projectId = project[name][0].id;
                                acc.projectIds.push(projectId);
                                acc.projectNames.push(name);
                            }
                        }
                        return acc;
                    },
                    { projectIds: [], projectNames: [] }
                );

                return {
                    projectId: result.projectIds.join(','),
                    projectName: result.projectNames.join(','),

                    //send the property received in config
                    ...(config.discovery_config_id && {
                        discovery_config_id: config.discovery_config_id,
                    }),
                };
            },
            [project]
        );

        const parseExtraFieldsDataFromResponse = useCallback((config) => {
            const { projectId, projectName, project_version } = config;

            const projectIdList = projectId.split(',');
            const projectVersionList = project_version?.split(',');
            const projectNameSet = new Set();
            const versionSet = new Set();

            projectName?.split(',').forEach((item, index) => {
                const [name, version] = item.split(';');
                const versionId = projectIdList[index];

                projectNameSet.add(name);
                if (
                    config.discovery_config_id &&
                    projectVersionList[index] !== undefined &&
                    version === undefined
                ) {
                    versionSet.add(versionId);
                } else if (version !== undefined) {
                    versionSet.add(versionId);
                }
            });
            return {
                projectName: [...projectNameSet],
                version: [...versionSet],
            };
        }, []);

        const changeProject = useCallback(
            (value, field) => {
                const { name } = field;
                const configurations = form.getFieldValue('configurations');
                let updatedConfiguration = [...configurations];
                updatedConfiguration[name] = {
                    ...updatedConfiguration[name],
                    version: undefined,
                };
                form.setFieldsValue({ configurations: updatedConfiguration });
            },
            [form]
        );

        const isProjectFieldDisabled = useCallback((fieldKey, form) => {
            const fieldConfiguration =
                form.getFieldValue('configurations')[fieldKey];
            return !fieldConfiguration || !fieldConfiguration.projectName;
        }, []);

        const extraFields = useMemo(
            () => [
                {
                    name: 'projectName',
                    colSpan: '12',
                    type: FieldTypes.MULTI_SELECT,
                    label: 'Project',
                    required: true,
                    options: projectList,
                    disabled: disableProjectFields,
                    props: {
                        mode: 'multiple',
                        autoFocus: !disableProjectFields,
                        onChange: changeProject,
                    },
                },
                {
                    name: 'version',
                    colSpan: '12',
                    type: FieldTypes.MULTI_SELECT,
                    label: 'Version',
                    required: false,
                    propsGenerator: (fieldKey) => ({
                        disabled:
                            isProjectFieldDisabled(fieldKey, form) ||
                            disableProjectFields,
                    }),
                    props: {
                        getFormListOptions: (fieldKey) => {
                            const fieldConfiguration =
                                form.getFieldValue('configurations')[fieldKey];
                            return getFilteredProjectOptions(
                                fieldConfiguration?.projectName
                            );
                        },
                    },
                },
            ],
            [
                disableProjectFields,
                form,
                getFilteredProjectOptions,
                projectList,
                changeProject,
                isProjectFieldDisabled,
            ]
        );

        return (
            <GenericMappingForm
                form={form}
                ref={ref}
                toolLoading={loading}
                getExtraFieldsData={getExtraFieldsData}
                extraFields={extraFields}
                formDefaultValues={initialValues}
                parseExtraFieldsDataFromResponse={
                    parseExtraFieldsDataFromResponse
                }
                {...props}
            />
        );
    }
);

export default DependencyTrackMappingForm;
